<template>
  <NavbarView />
  <div class="home-page">
    <!-- HERO SECTION -->
    <section class="hero d-flex align-items-center text-center text-white">
      <!-- Floating Icons -->
      <i class="bi bi-atom floating-icon icon1"></i>
      <i class="bi bi-lightning-charge floating-icon icon2"></i>
      <i class="bi bi-cpu floating-icon icon3"></i>

      <div class="container position-relative z-2">
        <h1 class="display-3 fw-bold glow-text">AXION</h1>
        <h2 class="fw-semibold text-pink mb-3">SCIENCE ACADEMY</h2>
        <p class="lead mb-4">
          No dream is too big, and no dreamer is too small.
        </p>

        <div class="d-flex justify-content-center gap-3 flex-wrap">
          <button class="btn btn-pink btn-lg px-4">
            <i class="bi bi-mortarboard-fill me-2"></i>
            Explore Courses
          </button>

          <button class="btn btn-outline-light btn-lg px-4">
            <i class="bi bi-telephone-fill me-2"></i>
            Contact Us
          </button>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section class="features py-5">
      <div class="container">
        <div class="row g-4 text-center">
          <div class="col-md-4">
            <div class="feature-card p-4">
              <i class="bi bi-laptop display-4 text-pink mb-3"></i>
              <h5 class="fw-bold">100% Online</h5>
              <p>Learn anytime, anywhere with our flexible online platform.</p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="feature-card p-4">
              <i class="bi bi-person-video3 display-4 text-pink mb-3"></i>
              <h5 class="fw-bold">Expert Mentors</h5>
              <p>Guidance from experienced science educators.</p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="feature-card p-4">
              <i class="bi bi-graph-up-arrow display-4 text-pink mb-3"></i>
              <h5 class="fw-bold">Proven Results</h5>
              <p>Helping students achieve excellence in science education.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <FooterView />
  </div>
</template>

<script>
import NavbarView from "@/components/NavbarView.vue";
import FooterView from "@/components/FooterView.vue";
export default {
  name: "HomePage",
  components: {
    NavbarView,
    FooterView,
  },
};
</script>

<style scoped>
/* ===== HERO SECTION ===== */
.hero {
  min-height: 100vh;
  background: linear-gradient(135deg, #0d0015, #2b0033, #000000);
  position: relative;
  overflow: hidden;
}

/* Animated Gradient Effect */
.hero::before {
  content: "";
  position: absolute;
  width: 200%;
  height: 200%;
  top: -50%;
  left: -50%;
  background: radial-gradient(
    circle,
    rgba(255, 0, 204, 0.15) 0%,
    transparent 60%
  );
  animation: rotateBg 20s linear infinite;
}

@keyframes rotateBg {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Floating Science Icons */
.floating-icon {
  position: absolute;
  font-size: 40px;
  color: #ff00cc;
  opacity: 0.15;
  animation: float 6s ease-in-out infinite;
}

.icon1 {
  top: 15%;
  left: 10%;
}
.icon2 {
  bottom: 20%;
  right: 15%;
  animation-delay: 2s;
}
.icon3 {
  top: 60%;
  left: 80%;
  animation-delay: 4s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Text Glow */
.glow-text {
  color: white;
  text-shadow: 0 0 10px #ff00cc, 0 0 20px #ff00cc, 0 0 40px #ff00cc;
}

.text-pink {
  color: #ff00cc;
}

/* Buttons */
.btn-pink {
  background: linear-gradient(45deg, #ff00cc, #9900ff);
  border: none;
  color: white;
  transition: 0.3s;
}

.btn-pink:hover {
  transform: translateY(-3px);
  box-shadow: 0 0 15px #ff00cc;
}

/* ===== FEATURES ===== */
.features {
  background: #120015;
  color: white;
}

.feature-card {
  background: linear-gradient(180deg, #2b0033, #0d0010);
  border-radius: 15px;
  transition: 0.3s;
  box-shadow: 0 0 15px rgba(255, 0, 204, 0.1);
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 0 25px rgba(255, 0, 204, 0.5);
}

/* ===== FOOTER ===== */
.footer {
  background: black;
  color: #aaa;
}

/* ============================= */
/* 📱 MOBILE RESPONSIVE STYLES  */
/* ============================= */

@media (max-width: 991.98px) {
  /* HERO TEXT SIZE REDUCTION */
  .hero h1 {
    font-size: 2.5rem !important;
  }

  .hero h2 {
    font-size: 1.3rem;
  }

  .hero p {
    font-size: 1rem;
    padding: 0 10px;
  }

  /* Reduce hero height for mobile */
  .hero {
    min-height: 90vh;
    padding-top: 80px; /* space for fixed navbar */
  }

  /* Reduce floating icon size */
  .floating-icon {
    font-size: 28px;
  }

  /* Adjust floating icon positions */
  .icon1 {
    top: 10%;
    left: 5%;
  }

  .icon2 {
    bottom: 15%;
    right: 5%;
  }

  .icon3 {
    top: 65%;
    left: 70%;
  }

  /* Buttons full width on small screens */
  .hero .btn {
    width: 100%;
    max-width: 300px;
  }

  /* Features spacing */
  .features {
    padding-top: 40px;
    padding-bottom: 40px;
  }

  .feature-card {
    padding: 25px 15px !important;
  }
}

/* Extra small devices */
@media (max-width: 576px) {
  .hero h1 {
    font-size: 2rem !important;
  }

  .glow-text {
    text-shadow: 0 0 5px #ff00cc, 0 0 10px #ff00cc;
  }

  .feature-card {
    border-radius: 10px;
  }
}
</style>
